/**
 * ASB606Finance — iter-238 (Feb 2026)
 *
 * User request: enter ASB606 Finance and see ONLY the two top-level cards
 * directly ("Financial Entry" + "Financial Records & Tracking"). The
 * older 5-card layout (Payment Records / Payments / Revenue Reports /
 * Pending Payments / Financial Statistics) has been removed — Payment
 * Records + Statistics live elsewhere and Revenue/Pending were dead links.
 *
 * Both cards share the same testids/routes previously used by PaymentsHub
 * so any downstream page continues to work unchanged. The `/finance/asb606
 * /payments` route still resolves (legacy) but is no longer linked from
 * here — the two entries below jump straight to the sub-hubs.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CreditCard, FilePlus2, ClipboardList,
  ArrowLeft, ArrowRight, Info,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { usePerms } from '../permissions/PermissionsContext';

const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';

export default function ASB606Finance() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const isAr = language === 'ar';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const CARDS = [
    {
      id: 'entry',
      number: 1,
      icon: FilePlus2,
      color: '#3B82F6',
      route: '/finance/asb606/payments/entry',
      titleAr: 'الإدراج المالي للشركة',
      titleEn: 'Financial Entry',
      descAr: 'إدراج الرواتب، المدفوعات، أصول الشركة، والمستحقات (إدخال البيانات)',
      descEn: 'Add salaries, company payments, assets, and liabilities (data entry)',
      testid: 'asb606-finance-entry',
    },
    {
      id: 'records',
      number: 2,
      icon: ClipboardList,
      color: '#10B981',
      route: '/finance/asb606/payments/records',
      titleAr: 'السجل والمتابعة المالية للشركة',
      titleEn: 'Financial Records & Tracking',
      descAr: 'عرض ومتابعة الرواتب، المدفوعات، الأصول، والمستحقات المسجّلة',
      descEn: 'View & track saved salaries, payments, assets, and liabilities',
      testid: 'asb606-finance-records',
    },
  ];

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <DashboardHeader
        title={language === 'ar' ? 'ASB606 (المالية)' : 'ASB606 (Finance)'}
        subtitle={language === 'ar' ? 'الإدارة المالية لمنصة ASB606' : 'ASB606 Financial Management'}
        icon={CreditCard}
        iconColor="#d4af37"
        showBackButton
        backPath="/dashboard/finance"
      />

      {/* Main Content */}
      <main className="relative z-10">
        <div className="border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <BreadcrumbNav items={[
              { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
              { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
              { label_ar: 'ASB606 (المالية)', label_en: 'ASB606 (Finance)', path: null },
            ]} />
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Title Section */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
              <img src={LOGO_TRIANGLE} alt="ASB606" className="w-12 h-12 object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {language === 'ar' ? 'ASB606 (المالية)' : 'ASB606 (Finance)'}
            </h1>
            <p className="text-[#71717A] text-sm">
              {language === 'ar' ? 'اختر بين إدراج بيانات جديدة أو عرض السجلات والمتابعة' : 'Add new data or view records & tracking'}
            </p>
          </div>

          {/* Two-card grid — same design language as the previous PaymentsHub */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="asb606-finance-hub">
            {visiblePages(CARDS, 'route').map((card, i) => {
              const Icon = card.icon;
              const Arrow = isAr ? ArrowLeft : ArrowRight;
              return (
                <motion.button
                  key={card.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  whileHover={{ y: -5, scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => navigate(card.route)}
                  className="group relative bg-white/[0.02] backdrop-blur-xl border rounded-2xl p-6 text-start transition-all overflow-hidden"
                  style={{ borderColor: `${card.color}30` }}
                  data-testid={card.testid}
                >
                  <div
                    className="absolute top-3 start-3 w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs border"
                    style={{ backgroundColor: `${card.color}25`, borderColor: `${card.color}60`, color: card.color }}
                  >
                    {card.number}
                  </div>

                  <div className="flex items-start gap-4 mt-4">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 shrink-0"
                      style={{ backgroundColor: `${card.color}15`, borderColor: `${card.color}40` }}
                    >
                      <Icon className="h-7 w-7" style={{ color: card.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                        {isAr ? card.titleAr : card.titleEn}
                        <Arrow className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: card.color }} />
                      </h3>
                      <p className="text-sm text-[#A1A1AA] leading-relaxed">
                        {isAr ? card.descAr : card.descEn}
                      </p>
                    </div>
                  </div>

                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{ background: `radial-gradient(circle at 50% 50%, ${card.color}10 0%, transparent 70%)` }}
                  />
                </motion.button>
              );
            })}
          </div>

          <div className="mt-6 bg-white/[0.02] border border-white/10 rounded-2xl p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0">
              <Info className="h-4 w-4 text-blue-400" />
            </div>
            <p className="text-xs text-[#A1A1AA] leading-relaxed">
              {isAr
                ? 'الإدراج والسجلات فُصلا في مسارين منفصلين — تحطّ البيانات في "الإدراج"، وتراجع كل شيء في "السجل والمتابعة".'
                : 'Entry and records are now split into two independent flows — add data in "Entry" and review everything in "Records & Tracking".'}
            </p>
          </div>
        </div>
      </main>

      {/* Footer Logo */}
      <div className="flex justify-center pb-12 pt-8">
        <img
          src={LOGO_TRIANGLE}
          alt="ASB606"
          className="w-12 h-12 object-contain opacity-20"
        />
      </div>
    </div>
  );
}
